import { apiCall } from '../axios';
import { IGenericApiResponse } from '../../utils/interfaces';
import { IShipment } from '../shipment/interfaces';
import {
    IOrderCreateModel,
    IOrderUpdateModel,
    IOrderResponseModel,
    OrderStatus,
    IShipmentDraftResponseModel,
    ICreateDraftFromOrdersModel,
    IUpdateDraftOrdersModel,
    IBuildShipmentFromDraftModel,
    IOrderReviewResponseModal,
    IMergeLocationsPayload,
    IDraftShipmentOrderModel,
    IBuildShipmentResponse,
} from './interfaces';
import { API_CONSTANTS } from '../../services/apiConstants';

const ordersApi = {
    createOrder: async (
        model: IOrderCreateModel,
    ): Promise<IOrderResponseModel> => {
        const response: IGenericApiResponse<IOrderResponseModel> =
            await apiCall(
                'post',
                `${API_CONSTANTS.mcs}${API_CONSTANTS.orders}`,
                model,
                true,
            );
        return response.data;
    },

    getOrderById: async (
        id: number,
        includeAllRelatedEntities = true,
    ): Promise<IOrderResponseModel> => {
        const response: IGenericApiResponse<IOrderResponseModel> =
            await apiCall(
                'get',
                `${API_CONSTANTS.mcs}${API_CONSTANTS.orders}/${id}?includeAllRelatedEntities=${includeAllRelatedEntities}`,
                {},
                true,
            );
        return response.data;
    },

    updateOrder: async (
        id: number,
        model: IOrderUpdateModel,
    ): Promise<IOrderResponseModel> => {
        const response: IGenericApiResponse<IOrderResponseModel> =
            await apiCall(
                'put',
                `${API_CONSTANTS.mcs}${API_CONSTANTS.orders}/${id}`,
                model,
                true,
            );
        return response.data;
    },

    deleteOrder: async (id: number): Promise<void> => {
        await apiCall(
            'delete',
            `${API_CONSTANTS.mcs}${API_CONSTANTS.orders}/${id}${API_CONSTANTS.permanent}`,
            {},
            true,
        );
    },

    getOrdersByCustomer: async (
        customerId: number,
        status?: OrderStatus,
        pageSize = 50,
        pageNumber = 1,
    ): Promise<IOrderResponseModel[]> => {
        let url = `${API_CONSTANTS.mcs}${API_CONSTANTS.orders}${API_CONSTANTS.customer}/${customerId}?pageSize=${pageSize}&pageNumber=${pageNumber}`;
        if (status) {
            url += `&status=${status}`;
        }
        const response: IGenericApiResponse<IOrderResponseModel[]> =
            await apiCall('get', url, {}, true);
        return response.data;
    },

    getOrdersByStatus: async (
        status: string,
        pageSize = 50,
        pageNumber = 1,
    ): Promise<IOrderResponseModel[]> => {
        const response: IGenericApiResponse<IOrderResponseModel[]> =
            await apiCall(
                'get',
                `${API_CONSTANTS.mcs}${API_CONSTANTS.orders}${API_CONSTANTS.status}/${status}?pageSize=${pageSize}&pageNumber=${pageNumber}`,
                {},
                true,
            );
        return response.data;
    },

    getUnassignedOrders: async (
        pageSize = 50,
        pageNumber = 1,
    ): Promise<IOrderResponseModel[]> => {
        const response: IGenericApiResponse<IOrderResponseModel[]> =
            await apiCall(
                'get',
                `${API_CONSTANTS.mcs}${API_CONSTANTS.orders}/unassigned?pageSize=${pageSize}&pageNumber=${pageNumber}`,
                {},
                true,
            );
        return response.data;
    },

    updateOrderStatus: async (
        id: number,
        newStatus: OrderStatus,
        notes?: string,
    ): Promise<IOrderResponseModel> => {
        let url = `${API_CONSTANTS.mcs}${API_CONSTANTS.orders}/${id}${API_CONSTANTS.status}?newStatus=${newStatus}`;
        if (notes) {
            url += `&notes=${encodeURIComponent(notes)}`;
        }
        const response: IGenericApiResponse<IOrderResponseModel> =
            await apiCall('put', url, {}, true);
        return response.data;
    },
};

const draftsApi = {
    createDraftFromOrders: async (
        model: ICreateDraftFromOrdersModel,
    ): Promise<IShipmentDraftResponseModel> => {
        const response: IGenericApiResponse<IShipmentDraftResponseModel> =
            await apiCall(
                'post',
                `${API_CONSTANTS.mcs}${API_CONSTANTS.shipmentDrafts}`,
                model,
                true,
            );
        return response.data;
    },

    getDrafts: async (
        pageSize = 50,
        pageNumber = 1,
    ): Promise<IShipmentDraftResponseModel[]> => {
        const response: IGenericApiResponse<IShipmentDraftResponseModel[]> =
            await apiCall(
                'get',
                `${API_CONSTANTS.mcs}${API_CONSTANTS.shipmentDrafts}?pageSize=${pageSize}&pageNumber=${pageNumber}`,
                {},
                true,
            );
        return response.data;
    },

    getDraftById: async (id: number): Promise<IShipmentDraftResponseModel> => {
        const response: IGenericApiResponse<IShipmentDraftResponseModel> =
            await apiCall(
                'get',
                `${API_CONSTANTS.mcs}${API_CONSTANTS.shipmentDrafts}/${id}`,
                {},
                true,
            );
        return response.data;
    },

    updateDraftOrders: async (
        id: number,
        model: IUpdateDraftOrdersModel,
    ): Promise<IShipmentDraftResponseModel> => {
        const response: IGenericApiResponse<IShipmentDraftResponseModel> =
            await apiCall(
                'put',
                `${API_CONSTANTS.mcs}${API_CONSTANTS.shipmentDrafts}/${id}`,
                model,
                true,
            );
        return response.data;
    },

    buildShipmentFromDraft: async (
        id: number,
        model?: IBuildShipmentFromDraftModel,
    ): Promise<IShipment> => {
        const response: IGenericApiResponse<IShipment> = await apiCall(
            'post',
            `${API_CONSTANTS.mcs}${API_CONSTANTS.shipmentDrafts}/${id}/build`,
            model || {},
            true,
        );
        return response.data;
    },

    getSuggestedOrders: async (
        draftId: number,
    ): Promise<IDraftShipmentOrderModel[]> => {
        const response: IGenericApiResponse<IDraftShipmentOrderModel[]> =
            await apiCall(
                'get',
                `${API_CONSTANTS.mcs}${API_CONSTANTS.shipmentDrafts}/${draftId}/suggested-orders`,
                {},
                true,
            );
        return response.data;
    },

    deleteDraft: async (id: number): Promise<void> => {
        await apiCall(
            'delete',
            `${API_CONSTANTS.mcs}${API_CONSTANTS.shipmentDrafts}/${id}`,
            {},
            true,
        );
    },
};

const reviewOrdersApi = {
    getOrdersToReview: (
        orderIds: number[],
    ): Promise<IOrderReviewResponseModal> => {
        const serializedOrderIds: string = orderIds.join(',');
        const params = new URLSearchParams({
            orderId: serializedOrderIds,
        });

        const baseUrl = `${API_CONSTANTS.mcs}${API_CONSTANTS.orders}`;
        const finalUrl = `${baseUrl}?${params.toString()}`;
        return apiCall('get', finalUrl);
    },
    mergeLocations: async (payload: IMergeLocationsPayload): Promise<void> => {
        await apiCall(
            'put',
            `${API_CONSTANTS.mcs}${API_CONSTANTS.orders}${API_CONSTANTS.locations}${API_CONSTANTS.configure}`,
            payload,
            true,
        );
    },
    buildShipment: async (
        orderIds: number[],
        draftId?: number | null,
    ): Promise<IBuildShipmentResponse> => {
        const payload = draftId
            ? { orderIds, shipmentId: draftId }
            : { orderIds };
        const response = await apiCall(
            'post',
            `${API_CONSTANTS.mcs}${API_CONSTANTS.shipmentDrafts}${API_CONSTANTS.buildShipment}`,
            payload,
            true,
        );
        return response.data as IBuildShipmentResponse;
    },
};

export const mcsApi = {
    orders: ordersApi,
    drafts: draftsApi,
    reviewOrders: reviewOrdersApi,
};
